import { t as ct } from "../lib/content.js";
import { PhotoCapture, warmReader } from "./PhotoCapture.jsx";
import { HW_MAX_SUBMISSIONS, canSubmitHomework, cleanSubmission, latestSubmission, pendingSubmissions } from "../lib/homework.js";
import { containsRudeWord } from "../lib/rude-words.js";

const { useRef, useState } = React;

// LB-381 (Alan, 27 Sep). A child photographs the sheet school sent home and sends the words to a
// parent, who checks them and adds them. The child's half is deliberately thin:
//
//   - it uses the SAME camera and the SAME reader as the parent's own Set homework step, so a
//     sheet reads exactly as well from a child's phone as from a parent's;
//   - it shows the words it read, because a child needs to know whether it read THEIR sheet —
//     but with no amber, no suggestions and no spelling advice. Checking the words is the
//     parent's job, and a child being told their spelling is wrong by an OCR misread is the
//     failure this avoids;
//   - a row the rude filter catches never reaches the screen (cleanSubmission).
//
// NO ADULT IS NAMED. A child may have several, and the app's own kid copy says "ask a parent",
// "ask your grown-up", "waiting on a parent" — never a name (Alan, 27 Sep: "we don't know the
// person is called Dad").
//
// Staging round (Alan, 28 Sep):
//   - the child says what kind of sheet it is first — spellings, or words in another language —
//     because the parent's review and the list it becomes need to know, and only the child is
//     looking at the sheet;
//   - that tap opens the phone's own camera/picker straight away (the input is clicked by the
//     tap itself, because iOS ignores one clicked later), so there is no second "take a photo"
//     screen in between, exactly as the parent's own photo step works since #588;
//   - a sheet a parent sent back stays on this card, with what the parent said, until the child
//     sends the next one. It used to vanish: the push said "not approved" and the app said nothing.
//
// Layout (Alan, 28 Sep): this is the HEADER of the Homework section on the Learn tab, not a card
// of its own. "Homework" sits left, "Add new homework" is a text CTA on the right of the same row,
// and the kind selector only appears under that row once tapped (and folds away on a second tap).
// The list tiles render beneath it. A full card sat between the game tiles and the panel a game
// opens in, so a child tapping Spelling found the test two cards further down.
export function HomeworkSubmit({ requests = [], onSend, kidTheme = {} }) {
  const [phase, setPhase] = useState("idle"); // idle | camera | check
  const [open, setOpen] = useState(false); // the kind selector is showing
  const [type, setType] = useState("spelling"); // spelling | language
  const [file, setFile] = useState(null);
  const [read, setRead] = useState(null); // { pairs, dropped }
  const fileRef = useRef(null);
  const waiting = pendingSubmissions(requests).length;
  const room = canSubmitHomework(requests);
  const latest = latestSubmission(requests);
  const returned = latest && latest.status === "rejected" ? latest : null;

  function start(kind) {
    setType(kind);
    warmReader();
    if (fileRef.current) fileRef.current.click();
  }
  function onPickFile(e) {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setFile(f);
    setPhase("camera");
  }
  function onWords({ pairs }) {
    const cleaned = cleanSubmission(pairs, containsRudeWord);
    if (!cleaned.pairs.length) { setPhase("idle"); setRead(null); setFile(null); return; }
    setRead(cleaned);
    setPhase("check");
  }
  function send() {
    if (!read) return;
    onSend(read.pairs, read.dropped, type);
    setOpen(false);
    setPhase("idle");
    setRead(null);
    setFile(null);
  }

  // The one input both kinds of sheet click, inside the tap (see start()).
  const picker = <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid="hw-submit-file" />;

  if (phase === "camera") {
    return (
      <div className="bg-white rounded-2xl p-3 mb-3" data-testid="hw-submit-camera">
        {picker}
        <PhotoCapture key={file ? `${file.name}-${file.size}-${file.lastModified}` : "pick"} initialFile={file} type={type} onWords={onWords} onCancel={() => { setPhase("idle"); setFile(null); }} />
      </div>
    );
  }

  if (phase === "check" && read) {
    return (
      <div className="bg-white rounded-2xl p-4 mb-3" data-testid="hw-submit-check">
        {picker}
        <div className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400">{ct("hwsubmit.read_title", "We read {n} word{plural}", { n: read.pairs.length, plural: read.pairs.length === 1 ? "" : "s" })}</div>
        <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
          {read.pairs.map((p, i) => (
            <div key={i} className="px-3 py-2 text-sm text-slate-900 border-b border-slate-100 last:border-b-0" data-testid="hw-submit-word">{p.b ? `${p.a} — ${p.b}` : p.a}</div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-2">{ct("hwsubmit.check_body", "Do these look like your sheet? A parent will check them properly before they are added.")}</p>
        <button onClick={send} className={`w-full mt-3 text-white text-sm font-bold rounded-lg py-3 ${kidTheme.primaryBtn || "bg-slate-900"}`} data-testid="hw-submit-send">{ct("hwsubmit.send", "Send to a parent")}</button>
        <button onClick={() => start(type)} className="w-full mt-1 text-slate-900 text-sm font-bold py-2.5" data-testid="hw-submit-retake">{ct("hwsubmit.retake", "Take it again")}</button>
      </div>
    );
  }

  return (
    <div data-testid="hw-submit-card">
      {picker}
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-sm font-extrabold text-slate-900">{ct("homework.kid.group_title", "Homework")}</div>
        {room && (
          <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} className="text-xs font-bold text-slate-900 underline underline-offset-2" data-testid="hw-submit-toggle">{open ? ct("hwsubmit.close", "Close") : ct("hwsubmit.add_cta", "Add new homework")}</button>
        )}
      </div>
      {returned && (
        <div className="rounded-lg px-3 py-2.5 mb-2" style={{ background: "#FEF0DD", color: "#8A5F00" }} data-testid="hw-submit-returned">
          <div className="text-sm font-extrabold">{ct("hwsubmit.returned_title", "A parent sent your sheet back")}</div>
          <div className="text-xs mt-0.5">{String(returned.parentNote || "").trim() || ct("hwsubmit.returned_default", "Take another picture and send it again.")}</div>
        </div>
      )}
      {waiting > 0 && (
        <div className="text-xs text-slate-500 mb-2" data-testid="hw-submit-waiting">{ct("hwsubmit.waiting", "{n} sheet{plural} waiting for a parent to look at.", { n: waiting, plural: waiting === 1 ? "" : "s" })}</div>
      )}
      {!room && (
        <div className="text-xs text-slate-500 mb-2" data-testid="hw-submit-full">{ct("hwsubmit.full", "You have {max} sheets waiting already — a parent needs to look at those first.", { max: HW_MAX_SUBMISSIONS })}</div>
      )}
      {room && open && (
        <div className="bg-white rounded-2xl p-4 mb-3" data-testid="hw-submit-kind">
          <div className="text-sm font-extrabold text-slate-900">{ct("hwsubmit.title", "Got a new sheet?")}</div>
          <div className="text-xs text-slate-500 mt-1">{ct("hwsubmit.body", "Take a picture of it and a parent will check the words and add it. What kind of sheet is it?")}</div>
          {/* The two kinds are named so that NEITHER label contains a game tile's name. Playwright
              matches accessible names by substring, and the suite opens the games with
              getByRole("button", { name: /Spelling/ }) and /Vocabulary/ — "Spellings" here sat
              beside the Spelling tile and made every one of those specs fail on the prod gate
              (27 Sep). check-homework holds this. */}
          <div className="flex gap-2 mt-3">
            <button onClick={() => start("spelling")} className={`flex-1 text-white text-sm font-bold rounded-lg py-3 ${kidTheme.primaryBtn || "bg-slate-900"}`} data-testid="hw-submit-type-spelling">{ct("hwsubmit.type_spelling", "English words")}</button>
            <button onClick={() => start("language")} className={`flex-1 text-white text-sm font-bold rounded-lg py-3 ${kidTheme.primaryBtn || "bg-slate-900"}`} data-testid="hw-submit-type-language">{ct("hwsubmit.type_language", "Another language")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
